import { FeedbackContext } from '@/feedback/context';
import * as $18n from '@alife/lazada-i18n';
import { useSetState, useUpdateEffect } from 'ahooks';
import React, { useContext, useRef } from 'react';
import { screenrecordDataURItoBlob } from '../utils';
import { PreparingElement, RecordingElement } from './element';

const initState = {
  show: '',
  countDown: 3,
  countUp: 0,
};

export const ScreenRecord = ({
  uploading,
  preCheck,
  beforeUpload,
  handleUpload,
}: {
  uploading: boolean;
  preCheck: () => boolean;
  beforeUpload: (file: any) => boolean;
  handleUpload: (file: any) => Promise<void>;
}) => {
  const { onScreenshotStart, onScreenshotEnd, disableScreenRecord } = useContext(FeedbackContext);
  const [state, setState] = useSetState(initState);

  const mediaRecorderRef = useRef<any>(null);
  const screenStreamRef = useRef<any>(null);
  const timerRef = useRef<any>(null);
  const chunks = useRef<any>([]);

  const prepareEle = useRef<any>(null);
  const recordEle = useRef<any>(null);

  useUpdateEffect(() => {
    if (!state.show) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    } else if (state.show === 'preparing') {
      prepareEle.current = PreparingElement.getInstance();
      prepareEle.current.create(state.countDown);
      timerRef.current = setInterval(() => {
        setState((prev) => {
          if (prev.countDown > 1) {
            prepareEle.current.update(prev.countDown - 1);
            return { ...prev, countDown: prev.countDown - 1 };
          } else {
            mediaRecorderRef.current.start(10);
            clearInterval(timerRef.current);
            prepareEle.current.remove();
            return { ...prev, show: 'recording' };
          }
        });
      }, 1000);
    } else if (state.show === 'recording') {
      recordEle.current = RecordingElement.getInstance();
      recordEle.current.create(state.countUp, stopRecording);
      timerRef.current = setInterval(() => {
        setState((prev) => {
          if (prev.countUp < 60) {
            recordEle.current.update(prev.countUp + 1);
            return { ...prev, countUp: prev.countUp + 1 };
          } else {
            clearInterval(timerRef.current);
            stopRecording();
            recordEle.current.remove();
            return { ...initState };
          }
        });
      }, 1000);
    }
  }, [state.show]);

  if (disableScreenRecord) return <></>;

  const startRecording = async () => {
    const check = preCheck();
    if (!check) return;
    onScreenshotStart?.();

    navigator.mediaDevices
      .getDisplayMedia({
        video: true,
        audio: true,
      })
      .then(async (stream) => {
        mediaRecorderRef.current = new MediaRecorder(stream);
        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.current.push(event.data);
          }
        };
        mediaRecorderRef.current.onstop = () => {
          if (screenStreamRef.current) {
            const tracks = screenStreamRef.current.getTracks();
            tracks.forEach((track) => track.stop());
          }
          setState({ ...initState });

          const blob = new Blob(chunks.current, { type: 'video/mp4' });
          // 转换Blob为File
          const time = Date.now();
          const recordingFile: any = new File([blob], `${time}.mp4`, {
            type: 'video/mp4',
          });
          const file: any = screenrecordDataURItoBlob(recordingFile, time);
          const pass = beforeUpload(file);
          if (!pass) return;
          handleUpload(file);
        };

        chunks.current = []; // Clear the chunks array before starting a new recording
        screenStreamRef.current = stream;

        // 开始录制的时候加入一个监听，当用户离开页面的时候提醒
        window.onbeforeunload = (e) => {
          e = e || window.event;
          if (e) {
            e.returnValue = '离开当前页面，录制会停止';
          }
          return '离开当前页面，录制会停止';
        };

        setState({ show: 'preparing', countDown: 3, countUp: 0 });
      })
      .catch(() => {
        onScreenshotEnd?.();
      });
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      onScreenshotEnd?.();
    }
    if (screenStreamRef.current) {
      const tracks = screenStreamRef.current.getTracks();
      tracks.forEach((track) => track.stop());
    }
    recordEle.current?.remove();
    setState(initState);
  };

  return <>
    <div
      className={`asc-feedback-screenshot ${uploading ? 'asc-feedback-screenshot-uploading' : ''}`}
      style={{ marginRight: 0 }}
      onClick={startRecording}
    >
      <div className="asc-feedback-screenshot-inner">
        <img
          style={{ width: 20 }}
          src={
            '//lzd-img-global.slatic.net/g/tps/imgextra/i3/O1CN01OzKVv51n6N8kYVPxT_!!6000000005040-55-tps-23-18.svg'
          }
        />
        <div>{/*#__PURE__*/$18n.get('asc-fb.feedback-form.screen.record', 'Record')}</div>
      </div>
    </div>
  </>;
};
